import React from 'react';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Volume2 } from 'lucide-react';
import { previewSound } from '../services/sounds';

const TaskDetailsTab = ({
  showCompletedTasks,
  onShowCompletedChange,
  moveCompletedToBottom,
  onMoveCompletedChange,
  showDescription,
  onShowDescriptionChange,
  showTaskNumbers,
  onShowTaskNumbersChange,
  soundEnabled,
  onSoundEnabledChange,
  soundType,
  onSoundTypeChange,
  onResetDefaults,
}) => (
  <div className="space-y-5 pt-4 min-h-[420px]">
    <SettingToggle
      id="show-completed"
      testId="switch-show-completed"
      label="Show Completed Tasks"
      description="Display or hide completed tasks"
      checked={showCompletedTasks}
      onChange={onShowCompletedChange}
    />
    <SettingToggle
      id="move-completed"
      testId="switch-move-completed"
      label="Move Completed to Bottom"
      description="Move checked tasks to bottom"
      checked={moveCompletedToBottom}
      onChange={onMoveCompletedChange}
    />
    <SettingToggle
      id="show-description"
      testId="switch-show-description"
      label="Show Description"
      description="Display task descriptions"
      checked={showDescription}
      onChange={onShowDescriptionChange}
    />
    <SettingToggle
      id="show-task-numbers"
      testId="switch-task-numbers"
      label="Show Task Numbers"
      description="Display numbered order for tasks"
      checked={showTaskNumbers}
      onChange={onShowTaskNumbersChange}
    />

    {/* Sound settings */}
    <div className="pt-3 border-t">
      <SettingToggle
        id="sound-enabled"
        testId="switch-sound-enabled"
        label="Turn on Sound"
        description="Play a sound when tasks are checked"
        checked={soundEnabled}
        onChange={onSoundEnabledChange}
      />
      {soundEnabled && (
        <div className="mt-3 ml-1 space-y-1.5">
          {[
            { value: 'ping', label: 'Plink!' },
            { value: 'pop', label: 'Pop!' },
            { value: 'pencil', label: 'Pencil' },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                soundType === opt.value
                  ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-800 border border-transparent'
              }`}
              data-testid={`sound-option-${opt.value}`}
            >
              <input
                type="radio"
                name="soundType"
                value={opt.value}
                checked={soundType === opt.value}
                onChange={() => onSoundTypeChange(opt.value)}
                className="accent-blue-600 w-4 h-4"
              />
              <span className="text-sm font-medium flex-1">{opt.label}</span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); previewSound(opt.value); }}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                title="Preview sound"
                data-testid={`preview-sound-${opt.value}`}
              >
                <Volume2 className="w-4 h-4" />
              </button>
            </label>
          ))}
        </div>
      )}
    </div>

    <div className="pt-4 border-t">
      <Button
        variant="outline"
        onClick={onResetDefaults}
        className="w-full text-sm"
        data-testid="reset-defaults-btn"
      >
        Return to Default Settings
      </Button>
    </div>
  </div>
);

const SettingToggle = ({ id, testId, label, description, checked, onChange }) => (
  <div className="flex items-center justify-between space-x-4">
    <div className="flex-1 space-y-1">
      <Label htmlFor={id} className="text-base font-medium">{label}</Label>
      <p className="text-sm text-slate-500">{description}</p>
    </div>
    <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

export default TaskDetailsTab;
